import { useState } from 'react'
import type { ExportDoc } from '../export/model'

/**
 * "Excel · PDF" — the extract pair every report page carries.
 *
 * `build` returns the ExportDoc for what is on screen RIGHT NOW, or null
 * while loading/failed/empty — the click then says there is nothing to
 * extract. The pair does NOT disable itself: build runs only at click time,
 * so the buttons cannot know beforehand, and they often sit in a card header
 * beside a refusal panel. A null must never be a silent dead click. It may
 * be async: the trend rasterizes its chart first.
 *
 * The generators are heavy (exceljs ~1 MB) and load on first click via
 * dynamic import — a reader who never extracts never downloads them.
 */
export function ExportButtons({ build }: { build: () => Promise<ExportDoc | null> | ExportDoc | null }) {
  const [busy, setBusy] = useState<'xlsx' | 'pdf' | null>(null)
  const [failed, setFailed] = useState(false)
  const [empty, setEmpty] = useState(false)

  const run = async (kind: 'xlsx' | 'pdf') => {
    if (busy) return
    setBusy(kind)
    setFailed(false)
    setEmpty(false)
    try {
      const doc = await build()
      if (!doc) {
        setEmpty(true)
        return
      }
      if (kind === 'xlsx') {
        const { exportExcel } = await import('../export/excel')
        await exportExcel(doc)
      } else {
        const { exportPdf } = await import('../export/pdf')
        exportPdf(doc)
      }
    } catch {
      setFailed(true)
    } finally {
      setBusy(null)
    }
  }

  const btn =
    'inline-flex items-center gap-1.5 rounded-md border border-(--color-line-strong) px-2.5 py-1.5 ' +
    'text-(length:--text-small) font-semibold text-(--color-ink-2) transition-colors ' +
    'hover:bg-(--color-surface-2) hover:text-(--color-ink) disabled:opacity-50'

  return (
    <div className="flex items-center gap-2" role="group" aria-label="Extract this view">
      <span className="eyebrow">Extract</span>
      <button type="button" className={btn} disabled={busy !== null} onClick={() => void run('xlsx')} title="Download exactly this view as a styled Excel workbook">
        <DownloadIcon />
        {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
      </button>
      <button type="button" className={btn} disabled={busy !== null} onClick={() => void run('pdf')} title="Download exactly this view as a print-ready PDF">
        <DownloadIcon />
        {busy === 'pdf' ? 'Preparing…' : 'PDF'}
      </button>
      {failed && (
        <span className="text-(length:--text-small) text-(--color-overdue)">Export failed — try again.</span>
      )}
      {empty && (
        <span className="text-(length:--text-small) text-(--color-ink-3)">Nothing on screen to extract.</span>
      )}
    </div>
  )
}

function DownloadIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden>
      <path
        d="M6 1v6.4M3.4 5.2 6 7.8l2.6-2.6M1.6 10.4h8.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
